import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, Calendar, AlertTriangle, Receipt, FileText, X, CheckCheck } from 'lucide-react';
import { dashboardAPI, notificationsAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';

export default function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverUnread, setServerUnread] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll léger du compteur de non-lues (badge à jour sans ouvrir le panneau).
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const r = await notificationsAPI.unreadCount();
        if (active) setServerUnread(r.data?.count || 0);
      } catch { /* noop */ }
    };
    poll();
    const iv = setInterval(poll, 90000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const [nRes, dRes] = await Promise.all([
        notificationsAPI.list().catch(() => ({ data: [] })),
        dashboardAPI.get().catch(() => ({ data: {} })),
      ]);
      const persisted = (nRes.data || []).map(n => ({
        id: n.id, source: 'db', type: n.type, title: n.titre, detail: n.message,
        date: n.created_at, read: !!n.lu,
      }));
      const alerts = (dRes.data?.alerts || []).map((a, i) => ({
        id: `alert-${i}`, source: 'alert', type: a.type, title: a.titre, detail: a.detail,
        date: a.date, responsable: a.responsable, read: false,
      }));
      const merged = [...persisted, ...alerts].sort(
        (x, y) => new Date(y.date || 0) - new Date(x.date || 0));
      setNotifications(merged);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const toggle = () => {
    if (!isOpen) loadNotifications();
    setIsOpen(!isOpen);
  };

  const markRead = async (n) => {
    if (n.read) return;
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    if (n.source === 'db') {
      setServerUnread(c => Math.max(0, c - 1));
      try { await notificationsAPI.read(n.id); } catch { /* noop */ }
    }
  };

  const handleReadAll = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setServerUnread(0);
    try { await notificationsAPI.readAll(); } catch { /* noop */ }
  };

  const listUnread = notifications.filter(n => !n.read).length;
  const unreadCount = isOpen ? listUnread : Math.max(listUnread, serverUnread);

  const getIcon = (type) => {
    switch (type) {
      case 'echeance': return <Clock className="w-4 h-4 text-[#F59E0B]" />;
      case 'audience': return <Calendar className="w-4 h-4 text-[#1E6091]" />;
      case 'facture': return <Receipt className="w-4 h-4 text-[#DC2626]" />;
      case 'courrier': return <FileText className="w-4 h-4 text-[#C9A227]" />;
      default: return <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={toggle}
        className="relative p-2 rounded-lg hover:bg-[var(--border-color)] transition-colors"
        data-testid="notifications-button"
      >
        <Bell className="w-[18px] h-[18px] text-[var(--text-muted)]" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#DC2626] text-[8px]  flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[380px] rounded-[12px] overflow-hidden z-50" style={{background:'var(--widget-bg)', border:'1px solid var(--border-color)', boxShadow:'0 15px 40px rgba(0,0,0,0.5)'}} data-testid="notifications-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{borderColor:'var(--border-color)'}}>
            <span className="text-[13px] font-medium ">Notifications</span>
            <div className="flex items-center gap-2">
              {listUnread > 0 && (
                <button onClick={handleReadAll} className="flex items-center gap-1 text-[11px] text-[#C9A227] hover:opacity-80" title="Tout marquer comme lu">
                  <CheckCheck className="w-3.5 h-3.5" /> Tout lire
                </button>
              )}
              <button onClick={() => setIsOpen(false)}>
                <X className="w-4 h-4 text-[var(--text-tertiary)]" />
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading && <div className="p-4 text-center text-[12px] text-[var(--text-tertiary)]">Chargement...</div>}

            {!loading && notifications.length === 0 && (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-[var(--text-tertiary)] mx-auto mb-2" />
                <div className="text-[12px] text-[var(--text-tertiary)]">Aucune notification</div>
              </div>
            )}

            {notifications.map(n => (
              <div
                key={n.id}
                onClick={() => markRead(n)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-[rgba(201,162,39,0.04)] ${!n.read ? '' : 'opacity-50'}`}
                style={{borderBottom:'1px solid var(--bg-tertiary)'}}
              >
                <div className="mt-0.5">{getIcon(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium ">{n.title}</div>
                  {n.detail && <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{n.detail}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-[var(--text-tertiary)]">{formatDate(n.date)}</span>
                    {n.responsable && <span className="text-[10px] text-[#C9A227]">{n.responsable}</span>}
                  </div>
                </div>
                {!n.read && <div className="w-2 h-2 rounded-full bg-[#C9A227] mt-1.5 flex-shrink-0"></div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
